/* The live view: a spectral ridge in motion.
 *
 * Same measurement as the waterfall on the Sound tab -- log frequency, dBFS,
 * the same colour scale from dsp.ts -- drawn as a run of lit ridges flowing
 * away from the viewer. Newest is nearest and largest; six seconds of history
 * recede toward a horizon. It is a spectrogram in perspective, not a new
 * quantity, so a shape learned here reads the same on the stored audio.
 *
 * Written against WebGL2 directly. A scene graph library would be the largest
 * dependency in the project for one screen's worth of geometry, and hls.js is
 * already most of the JavaScript this page loads. There are no vertex buffers
 * at all for the ridges: gl_VertexID and gl_InstanceID carry the whole mesh,
 * and the spectrum lives in a texture the shader reads its own heights from.
 *
 * Occlusion is the depth buffer's job rather than the painter's algorithm --
 * that is what keeps this at four draw calls a frame instead of one per ridge,
 * which is the difference between smooth and not on the phone this gets used
 * on. If WebGL2 is missing or the context is lost, the caller's fallback runs
 * instead; nothing here is load-bearing for hearing the sound.
 */
import { useEffect, useRef } from 'react'
import { F_MIN, LUT, axisTicks } from '../dsp'

/* Six seconds of history at 20 columns a second. Long enough to see a passing
 * vessel build, short enough that the near ridges still move visibly -- a
 * longer window turns the front of the picture into a slow crawl. */
const ROWS = 120
const COLUMN_MS = 50

/* Points across the frequency axis. The FFT gives 2048 bins; 220 log-spaced
 * points is more than a phone can resolve and keeps the vertex count flat
 * regardless of fftSize. */
const N = 220

const DEEP = '#04150f'

/* --------------------------------------------------------------- shaders */

const RIDGE_VERT = `#version 300 es
precision highp float;

uniform sampler2D u_spec;   // R8, N x ROWS, one column of levels per row
uniform sampler2D u_lut;    // 256x1, the dsp.ts colour scale
uniform int   u_n;
uniform int   u_rows;
uniform int   u_head;       // ring index of the newest column
uniform float u_frac;       // 0..1 between columns, so the flow is continuous
uniform float u_amp;
uniform float u_thick;
uniform int   u_mode;       // 0 skirt, 1 crest, 2 glow

out vec3  v_col;
out float v_edge;
out float v_gain;

const vec3 DEEP = vec3(0.016, 0.082, 0.059);

void main() {
  int i    = gl_VertexID >> 1;
  int side = gl_VertexID & 1;
  int age  = gl_InstanceID;          // 0 = newest = nearest

  float u = float(i) / float(u_n - 1);

  // Ring buffer. Sampled at the exact texel centre so LINEAR filtering across
  // frequency never bleeds between two different moments in time.
  int r = ((u_head - age) % u_rows + u_rows) % u_rows;
  float lvl = texture(u_spec, vec2(u, (float(r) + 0.5) / float(u_rows))).r;

  // u_frac slides every ridge back by a fraction of a step between columns.
  // Without it the picture advances 20 times a second in visible jumps; with
  // it the geometry moves every frame and the data still arrives on its own
  // clock.
  float z = (float(age) + u_frac) / float(u_rows);
  float s = 1.0 / (1.0 + z * 2.1);   // foreshortening

  float x     = (u - 0.5) * 2.0 * s;
  float base  = -0.74 + z * 1.12;    // ridges lift toward the horizon
  float crest = base + lvl * u_amp * s;

  float y;
  if (u_mode == 0) {
    y = (side == 0) ? crest : base;
    v_edge = 0.0;
  } else {
    float t = u_thick * (0.35 + 0.65 * s);
    // Only genuinely loud bins bloom. Squared, so the glow is a property of
    // the top of the range rather than a wash over everything.
    if (u_mode == 2) t *= (0.5 + 3.0 * lvl * lvl);
    y = crest + ((side == 0) ? t : -t);
    v_edge = (side == 0) ? 1.0 : -1.0;
  }

  vec3 c = texture(u_lut, vec2(lvl, 0.5)).rgb;
  c = mix(c, DEEP, z * 0.55);        // distance recedes into the background

  if (u_mode == 0) {
    // Interpolated down the skirt: lit at the crest, background at the base.
    // Opaque either way, because this is what occludes the ridges behind it.
    v_col  = mix(DEEP, c, (side == 0) ? 0.42 : 0.0);
    v_gain = 1.0;
  } else if (u_mode == 1) {
    v_col  = min(c * 1.25, vec3(1.0));
    v_gain = 1.0;
  } else {
    v_col  = c;
    v_gain = 0.34 * lvl * (1.0 - z * 0.7);
  }

  gl_Position = vec4(x, y, z * 1.9 - 0.95, 1.0);
}`

const RIDGE_FRAG = `#version 300 es
precision highp float;
in vec3  v_col;
in float v_edge;
in float v_gain;
out vec4 o;
void main() {
  float f = 1.0 - abs(v_edge);
  f = f * f;                                     // soft edge on the ribbons
  float a = v_gain * ((abs(v_edge) > 0.001) ? f : 1.0);
  o = vec4(v_col * a, a);                        // premultiplied
}`

/* The backdrop is drawn rather than set as a CSS colour: the ridges fade into
 * it with depth, so it has to be the same surface they are composited onto or
 * the far end of the picture shows a seam. */
const BG_VERT = `#version 300 es
void main() {
  vec2 p = vec2((gl_VertexID == 1) ? 3.0 : -1.0,
                (gl_VertexID == 2) ? 3.0 : -1.0);
  gl_Position = vec4(p, 0.0, 1.0);
}`

const BG_FRAG = `#version 300 es
precision highp float;
uniform vec2 u_res;
out vec4 o;
void main() {
  vec2 uv = gl_FragCoord.xy / u_res;
  float d = distance(uv, vec2(0.5, 0.82));       // light sits at the horizon
  float g = exp(-d * d * 3.2);
  o = vec4(mix(vec3(0.016, 0.082, 0.059), vec3(0.043, 0.208, 0.161), g * 0.85), 1.0);
}`

const SPARK_VERT = `#version 300 es
in vec2  a_pos;
in float a_life;
in float a_size;
out float v_life;
void main() {
  v_life = a_life;
  gl_Position = vec4(a_pos, -0.95, 1.0);
  gl_PointSize = a_size * (0.35 + 0.65 * a_life);
}`

const SPARK_FRAG = `#version 300 es
precision highp float;
in float v_life;
out vec4 o;
void main() {
  float r = length(gl_PointCoord - 0.5) * 2.0;
  if (r > 1.0) discard;
  float f = (1.0 - r) * (1.0 - r) * pow(v_life, 1.4);
  o = vec4(vec3(0.902, 0.224, 0.149) * f, f);    // Pulse
}`

/* ----------------------------------------------------------------- helpers */

function compile(gl: WebGL2RenderingContext, vs: string, fs: string): WebGLProgram | null {
  const mk = (type: number, src: string) => {
    const sh = gl.createShader(type)
    if (!sh) return null
    gl.shaderSource(sh, src)
    gl.compileShader(sh)
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
      // Logged rather than thrown: a shader that will not build is a reason to
      // fall back to the 2D view, not a reason to take the page down while
      // someone is listening.
      console.error('ridgeline shader:', gl.getShaderInfoLog(sh))
      gl.deleteShader(sh)
      return null
    }
    return sh
  }
  const v = mk(gl.VERTEX_SHADER, vs)
  const f = mk(gl.FRAGMENT_SHADER, fs)
  if (!v || !f) return null
  const p = gl.createProgram()
  if (!p) return null
  gl.attachShader(p, v)
  gl.attachShader(p, f)
  gl.linkProgram(p)
  gl.deleteShader(v)
  gl.deleteShader(f)
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
    console.error('ridgeline link:', gl.getProgramInfoLog(p))
    gl.deleteProgram(p)
    return null
  }
  return p
}

/** Frequency axis, low to high, left to right.
 *
 *  `axisTicks` returns fractions from the top for the vertical waterfall; the
 *  ridge lays the same log axis on its near edge, so the fraction is flipped
 *  rather than a second mapping being invented that could drift from it. */
function Axis({ sampleRate }: { sampleRate: number }) {
  return (
    <div className="ridge-axis" aria-hidden="true">
      {axisTicks(sampleRate).map((t) => (
        <span key={t.label} style={{ left: `${(1 - t.frac) * 100}%` }}>
          {t.label}
        </span>
      ))}
    </div>
  )
}

/* -------------------------------------------------------------- component */

interface Spark { x: number; y: number; vx: number; vy: number; life: number; size: number }

export function Ridgeline({
  analyser, height = 300, onUnsupported, onRange,
}: {
  analyser: AnalyserNode | null
  height?: number
  /** Called once if WebGL2 is unavailable or the context is lost, so the
   *  caller can put the 2D waterfall back. */
  onUnsupported?: () => void
  /** The range currently being drawn, so the legend can state it. */
  onRange?: (lo: number, hi: number) => void
}) {
  const ref = useRef<HTMLCanvasElement>(null)
  // Held in a ref as well as being called: the effect must not re-run when the
  // caller's identity changes, or a context loss would restart the renderer.
  const unsupported = useRef(onUnsupported)
  unsupported.current = onUnsupported
  const onRangeRef = useRef(onRange)
  onRangeRef.current = onRange

  useEffect(() => {
    const canvas = ref.current
    if (!canvas || !analyser) return

    const gl = canvas.getContext('webgl2', {
      alpha: false, antialias: true, depth: true, premultipliedAlpha: true,
      powerPreference: 'low-power',
    })
    if (!gl) {
      unsupported.current?.()
      return
    }

    const ridge = compile(gl, RIDGE_VERT, RIDGE_FRAG)
    const bg = compile(gl, BG_VERT, BG_FRAG)
    const spark = compile(gl, SPARK_VERT, SPARK_FRAG)
    if (!ridge || !bg || !spark) {
      unsupported.current?.()
      return
    }

    const U = {
      spec: gl.getUniformLocation(ridge, 'u_spec'),
      lut: gl.getUniformLocation(ridge, 'u_lut'),
      n: gl.getUniformLocation(ridge, 'u_n'),
      rows: gl.getUniformLocation(ridge, 'u_rows'),
      head: gl.getUniformLocation(ridge, 'u_head'),
      frac: gl.getUniformLocation(ridge, 'u_frac'),
      amp: gl.getUniformLocation(ridge, 'u_amp'),
      thick: gl.getUniformLocation(ridge, 'u_thick'),
      mode: gl.getUniformLocation(ridge, 'u_mode'),
    }
    const uRes = gl.getUniformLocation(bg, 'u_res')

    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1)

    // Spectrum ring buffer, one row per column of history.
    const specTex = gl.createTexture()
    gl.bindTexture(gl.TEXTURE_2D, specTex)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8, N, ROWS, 0, gl.RED,
                  gl.UNSIGNED_BYTE, new Uint8Array(N * ROWS))
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)

    // The colour scale, straight from dsp.ts so this and the Sound tab cannot
    // drift apart.
    const lutTex = gl.createTexture()
    gl.bindTexture(gl.TEXTURE_2D, lutTex)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB8, 256, 1, 0, gl.RGB,
                  gl.UNSIGNED_BYTE, new Uint8Array(LUT.buffer.slice(0)))
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)

    const MAX_SPARKS = 90
    const sparkData = new Float32Array(MAX_SPARKS * 4)   // x, y, life, size
    const sparkBuf = gl.createBuffer()
    const sparkVao = gl.createVertexArray()
    gl.bindVertexArray(sparkVao)
    gl.bindBuffer(gl.ARRAY_BUFFER, sparkBuf)
    gl.bufferData(gl.ARRAY_BUFFER, sparkData.byteLength, gl.DYNAMIC_DRAW)
    const aPos = gl.getAttribLocation(spark, 'a_pos')
    const aLife = gl.getAttribLocation(spark, 'a_life')
    const aSize = gl.getAttribLocation(spark, 'a_size')
    gl.enableVertexAttribArray(aPos)
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 16, 0)
    gl.enableVertexAttribArray(aLife)
    gl.vertexAttribPointer(aLife, 1, gl.FLOAT, false, 16, 8)
    gl.enableVertexAttribArray(aSize)
    gl.vertexAttribPointer(aSize, 1, gl.FLOAT, false, 16, 12)
    gl.bindVertexArray(null)

    /* Log-spaced frequency points, as bin ranges rather than single bins. At
     * the top of the axis one point covers hundreds of FFT bins, and taking
     * the maximum over the range keeps a narrow tonal visible where an average
     * would bury it in the noise either side of it. */
    const bins = analyser.frequencyBinCount
    const sampleRate = analyser.context.sampleRate
    const nyq = sampleRate / 2
    const binLo = new Int32Array(N)
    const binHi = new Int32Array(N)
    for (let i = 0; i < N; i++) {
      const f0 = F_MIN * Math.pow(nyq / F_MIN, Math.max(0, i - 0.5) / (N - 1))
      const f1 = F_MIN * Math.pow(nyq / F_MIN, Math.min(N - 1, i + 0.5) / (N - 1))
      const b0 = Math.round((f0 / nyq) * (bins - 1))
      const b1 = Math.round((f1 / nyq) * (bins - 1))
      binLo[i] = Math.min(bins - 1, Math.max(0, b0))
      binHi[i] = Math.min(bins - 1, Math.max(binLo[i]!, b1))
    }

    const db = new Float32Array(bins)
    const col = new Uint8Array(N)
    const raw = new Float32Array(N)
    const sorted = new Float32Array(N)

    /* The scale is measured, not assumed.
     *
     * This used to be a constant pair, -95..-20 dBFS, chosen for "an
     * uncalibrated recorder in a quiet room". This fleet's hydrophone is
     * nowhere near that: with a source held against the sensor the live stream
     * decodes at -56 dBFS RMS broadband, which spread across 2048 FFT bins is
     * about -89 per bin, and with the room quiet it is 25 dB lower again. Both
     * painted as the darkest colour on the scale, so the view stayed black
     * while real, loud audio was arriving -- indistinguishable from a broken
     * stream, which is exactly how it was reported.
     *
     * Percentiles per column, then eased, so the picture adapts to a passing
     * vessel over about a second rather than flickering with every frame. The
     * numbers are published to the legend because a scale that moves and does
     * not say so cannot be compared with anything. */
    let lo = -100
    let hi = -40
    let primed = false
    let reported = 0
    const MIN_SPAN = 26

    let head = 0
    let lastColumn = 0
    let lastFrame = 0
    let ema = 0
    let lastSpark = 0
    const sparks: Spark[] = []
    const calm = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false

    const dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1))
    let w = 0
    let h = 0
    const resize = () => {
      const rect = canvas.getBoundingClientRect()
      const nw = Math.max(64, Math.floor(rect.width * dpr))
      const nh = Math.max(64, Math.floor(height * dpr))
      if (nw === w && nh === h) return
      w = canvas.width = nw
      h = canvas.height = nh
      gl.viewport(0, 0, w, h)
    }
    resize()
    const ro = new ResizeObserver(resize)
    ro.observe(canvas)

    let raf = 0
    let lost = false
    const onLost = (e: Event) => {
      // Without preventDefault the context can never be restored, and the
      // browser drops it under memory pressure on exactly the phone this is
      // meant to run on.
      e.preventDefault()
      lost = true
      cancelAnimationFrame(raf)
      unsupported.current?.()
    }
    canvas.addEventListener('webglcontextlost', onLost)

    const pushColumn = (now: number) => {
      analyser.getFloatFrequencyData(db)

      for (let i = 0; i < N; i++) {
        let m = -Infinity
        for (let b = binLo[i]!; b <= binHi[i]!; b++) {
          const v = db[b]!
          if (v > m) m = v
        }
        // Silent bins come back as -Infinity, which would poison the scale.
        raw[i] = Number.isFinite(m) ? m : -160
      }

      // Percentiles of this column. p20 rather than the minimum so a single
      // dead bin does not set the floor; p995 rather than the maximum so one
      // spike does not push everything else into the dark.
      sorted.set(raw)
      sorted.sort()
      const q = (f: number) => sorted[Math.min(N - 1, Math.max(0, Math.round(f * (N - 1))))]!
      const tLo = q(0.2) - 4
      const tHi = Math.max(q(0.995) + 6, tLo + MIN_SPAN)

      if (!primed) {
        lo = tLo
        hi = tHi
        primed = true
      } else {
        // Rises quickly, falls back slowly: catching a vessel as it arrives
        // matters more than releasing after it has gone, and a fast release
        // makes the background breathe.
        lo += (tLo - lo) * (tLo < lo ? 0.15 : 0.04)
        hi += (tHi - hi) * (tHi > hi ? 0.15 : 0.03)
      }
      if (hi - lo < MIN_SPAN) hi = lo + MIN_SPAN
      const span = hi - lo

      let sum = 0
      let peak = 0
      let peakAt = 0
      for (let i = 0; i < N; i++) {
        let t = (raw[i]! - lo) / span
        t = t < 0 ? 0 : t > 1 ? 1 : t
        col[i] = (t * 255) | 0
        sum += t
        if (t > peak) { peak = t; peakAt = i }
      }

      // Twice a second is enough for a legend and avoids a React render per
      // column.
      if (onRangeRef.current && now - reported > 500) {
        reported = now
        onRangeRef.current(Math.round(lo), Math.round(hi))
      }

      head = (head + 1) % ROWS
      gl.bindTexture(gl.TEXTURE_2D, specTex)
      gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, head, N, 1, gl.RED,
                       gl.UNSIGNED_BYTE, col)

      /* A transient is a jump above where the last few seconds have been
       * sitting, not a level. Red is the moment of detection in this palette
       * and nothing else, so the threshold is deliberately high enough that a
       * steady loud vessel does not sparkle -- only the onset does. */
      const broad = sum / N
      ema = ema === 0 ? broad : ema + (broad - ema) * 0.06
      if (!calm && broad - ema > 0.055 && now - lastSpark > 260 &&
          sparks.length < MAX_SPARKS - 8) {
        lastSpark = now
        const x = (peakAt / (N - 1) - 0.5) * 2
        const y = -0.74 + peak * 0.55
        const n = 3 + Math.floor(Math.random() * 5)
        for (let k = 0; k < n; k++) {
          sparks.push({
            x, y,
            vx: (Math.random() - 0.5) * 0.5,
            vy: 0.25 + Math.random() * 0.55,
            life: 1,
            size: (6 + Math.random() * 10) * dpr,
          })
        }
      }
    }

    const frame = (now: number) => {
      raf = requestAnimationFrame(frame)
      if (lost || !w || !h) return

      const dt = lastFrame ? Math.min(0.05, (now - lastFrame) / 1000) : 0
      lastFrame = now
      if (!lastColumn) lastColumn = now
      // Catch up rather than drift, but never more than a few columns: a
      // backgrounded tab returning after a minute should resume, not spend a
      // second replaying history nobody heard.
      let guard = 0
      while (now - lastColumn >= COLUMN_MS && guard++ < 4) {
        lastColumn += COLUMN_MS
        pushColumn(now)
      }
      if (now - lastColumn > COLUMN_MS * 4) lastColumn = now
      const frac = Math.min(1, (now - lastColumn) / COLUMN_MS)

      gl.bindVertexArray(null)
      gl.disable(gl.DEPTH_TEST)
      gl.depthMask(false)
      gl.disable(gl.BLEND)
      gl.useProgram(bg)
      gl.uniform2f(uRes, w, h)
      gl.drawArrays(gl.TRIANGLES, 0, 3)

      gl.useProgram(ridge)
      gl.activeTexture(gl.TEXTURE0)
      gl.bindTexture(gl.TEXTURE_2D, specTex)
      gl.uniform1i(U.spec, 0)
      gl.activeTexture(gl.TEXTURE1)
      gl.bindTexture(gl.TEXTURE_2D, lutTex)
      gl.uniform1i(U.lut, 1)
      gl.uniform1i(U.n, N)
      gl.uniform1i(U.rows, ROWS)
      gl.uniform1i(U.head, head)
      gl.uniform1f(U.frac, frac)
      gl.uniform1f(U.amp, 0.55)
      gl.uniform1f(U.thick, 2.6 / h)

      gl.enable(gl.BLEND)
      gl.enable(gl.DEPTH_TEST)
      gl.clear(gl.DEPTH_BUFFER_BIT)

      // Skirts first and they alone write depth: they are what hides the
      // ridges behind them. Crest and glow test against that and leave it
      // alone, so a soft edge never punches a hole in the ridge behind it.
      gl.depthFunc(gl.LESS)
      gl.depthMask(true)
      gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA)
      gl.uniform1i(U.mode, 0)
      gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, N * 2, ROWS)

      gl.depthFunc(gl.LEQUAL)
      gl.depthMask(false)
      gl.blendFunc(gl.ONE, gl.ONE)
      gl.uniform1i(U.mode, 2)
      gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, N * 2, ROWS)

      gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA)
      gl.uniform1i(U.mode, 1)
      gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, N * 2, ROWS)

      if (sparks.length) {
        for (let i = sparks.length - 1; i >= 0; i--) {
          const p = sparks[i]!
          p.x += p.vx * dt
          p.y += p.vy * dt
          p.vy -= 0.35 * dt
          p.life -= dt / 0.9
          if (p.life <= 0) sparks.splice(i, 1)
        }
        const n = Math.min(sparks.length, MAX_SPARKS)
        for (let i = 0; i < n; i++) {
          const p = sparks[i]!
          sparkData[i * 4] = p.x
          sparkData[i * 4 + 1] = p.y
          sparkData[i * 4 + 2] = p.life
          sparkData[i * 4 + 3] = p.size
        }
        gl.disable(gl.DEPTH_TEST)
        gl.blendFunc(gl.ONE, gl.ONE)
        gl.useProgram(spark)
        gl.bindVertexArray(sparkVao)
        gl.bindBuffer(gl.ARRAY_BUFFER, sparkBuf)
        gl.bufferSubData(gl.ARRAY_BUFFER, 0, sparkData, 0, n * 4)
        gl.drawArrays(gl.POINTS, 0, n)
        gl.bindVertexArray(null)
      }
    }
    raf = requestAnimationFrame(frame)

    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
      canvas.removeEventListener('webglcontextlost', onLost)
      gl.deleteProgram(ridge)
      gl.deleteProgram(bg)
      gl.deleteProgram(spark)
      gl.deleteTexture(specTex)
      gl.deleteTexture(lutTex)
      gl.deleteBuffer(sparkBuf)
      gl.deleteVertexArray(sparkVao)
      // The canvas is about to be discarded with the component; asking for the
      // context to be dropped now frees the GPU memory immediately rather than
      // whenever the collector gets to it. Someone opening and closing Listen
      // repeatedly on a phone should not accumulate contexts.
      gl.getExtension('WEBGL_lose_context')?.loseContext()
    }
  }, [analyser, height])

  return (
    <div className="ridge-wrap" style={{ height, background: DEEP }}>
      <canvas ref={ref} className="ridge-canvas" />
      {analyser && <Axis sampleRate={analyser.context.sampleRate} />}
    </div>
  )
}
